import React, { useCallback, useState } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { WidgetGrid } from './WidgetGrid.jsx';
import { EditModeControls } from './EditModeControls.jsx';
import { WidgetGallery } from './WidgetGallery.jsx';
import { WidgetSettingsSheet } from './WidgetSettingsSheet.jsx';
import { updateItemProps } from './layoutModel.js';
import { useDashboardEditor } from './useDashboardEditor.js';
import { useDashboardLayout } from './useDashboardLayout.js';
import { widgetRegistry } from './widgetRegistry.js';

/**
 * One component = a whole customizable dashboard (Beta-1 plan P3-20): load the viewer's layout
 * (or the role default), Customize / Done / Cancel / Add widget / Reset, the gallery, the grid.
 * Pages keep their own header, banners and data providers around it.
 *
 *   <CustomizableDashboard
 *     dashboardKey={DASHBOARD_KEYS.drewOe}
 *     viewer={useDashboardViewer()}          // { layoutUid, orgId, role, readOnly }
 *     toolbar={<ExportButtons />}            // optional, left of the controls
 *     widgetContext={{ featureHugo }}        // optional availability context
 *     onDropped={(ids) => notify(...)}       // optional toast for widgets no longer in the registry
 *     onOpenSettings={(i) => …}              // P3-18
 *   />
 */
export function CustomizableDashboard({
  dashboardKey,
  viewer,
  registry = widgetRegistry,
  toolbar = null,
  widgetContext = null,
  onDropped,
  onOpenSettings,
  scopeOptions = [],
  renderEmpty,
  rowHeight,
  gutter,
  sx,
}) {
  const { layoutUid, orgId, role, readOnly } = viewer || {};
  const saved = useDashboardLayout({ dashboardKey, uid: layoutUid, orgId, role, registry, widgetContext, readOnly, onDropped });
  const onDone = useCallback((layout, { dirty }) => (dirty ? saved.save(layout) : undefined), [saved.save]);
  const editor = useDashboardEditor(saved.layout, { onDone });
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [settingsFor, setSettingsFor] = useState(null); // item id whose settings sheet is open (P3-18)

  // Settings save into the item's props; outside edit mode the layout is written right away
  // (one write), inside edit mode it rides along with Done / Cancel.
  const openSettings = useCallback((i) => (onOpenSettings ? onOpenSettings(i) : setSettingsFor(i)), [onOpenSettings]);
  const saveSettings = useCallback(
    (props) => {
      const i = settingsFor;
      setSettingsFor(null);
      if (!i) return;
      const next = updateItemProps(editor.layout, i, props);
      editor.setLayout(next);
      if (!editor.editing && !readOnly) saved.save(next);
    },
    [settingsFor, editor.layout, editor.setLayout, editor.editing, readOnly, saved.save]
  );
  const settingsItem = settingsFor ? editor.layout.items.find((it) => it.i === settingsFor) || null : null;
  const settingsWidget = settingsItem ? registry.get(settingsItem.widgetId) : null;

  const handleReset = useCallback(async () => {
    const layout = await saved.reset();
    editor.replace(layout);
  }, [saved.reset, editor.replace]);

  if (saved.loading) {
    return (
      <Box sx={sx}>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={160} sx={{ flex: 1, borderRadius: 3 }} />
          ))}
        </Stack>
        <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={sx} className="customizable-dashboard" data-dashboard-key={dashboardKey}>
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        {toolbar}
        <Box sx={{ flex: 1 }} />
        {readOnly ? (
          <Typography variant="caption" color="text.secondary">
            Viewing this member&apos;s dashboard as they arranged it
          </Typography>
        ) : (
          <EditModeControls editor={editor} onAdd={() => setGalleryOpen(true)} onReset={handleReset} />
        )}
      </Stack>

      <WidgetGrid
        layout={editor.layout}
        registry={registry}
        editing={editor.editing}
        role={role}
        dashboard={dashboardKey}
        widgetContext={widgetContext}
        rowHeight={rowHeight}
        gutter={gutter}
        onLayoutChange={editor.setLayout}
        onRemove={editor.remove}
        onRequestEdit={readOnly ? undefined : editor.begin}
        highlightId={editor.lastAdded}
        onHighlightEnd={editor.clearAdded}
        onOpenSettings={readOnly ? undefined : openSettings}
        renderEmpty={renderEmpty}
      />

      {!readOnly && (
        <WidgetSettingsSheet open={Boolean(settingsItem)} widget={settingsWidget} item={settingsItem} scopeOptions={scopeOptions} onClose={() => setSettingsFor(null)} onSave={saveSettings} />
      )}

      {!readOnly && (
        <WidgetGallery
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          editor={editor}
          registry={registry}
          role={role}
          dashboard={dashboardKey}
          widgetContext={widgetContext}
          onAdded={() => setGalleryOpen(false)}
        />
      )}
    </Box>
  );
}

export default CustomizableDashboard;
